import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./exceptions.css";
import DynamicGrid from "../../components/dynamicGrid/DynamicGrid";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import AppState from "../../AppState";
import { FooterTabPosition } from "../../AppState/footerTabPosition";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import {
  determineTab,
  filterCriteriaValue,
  username,
  userTerminal,
  DateHelper,
} from "../../helpers";
// useLocation already imported above
import { getExceptionList } from "../../API/getExceptionList";
import {
  filterFromSelector,
  pageSelector,
  sortSelector,
} from "../../AppState/selectors";
import {
  ALL_OSD_TYPES,
  DAMAGE_OSD_TYPE,
  ETab,
  MISDELIVERY_OSD_TYPES,
  ON_HAND_DELIVERY_OSD_TYPES,
  OVERAGE_OSD_TYPES,
  SEARCHABLE_FIELDS,
  SHORTAGE_OSD_TYPES,
} from "../../constants";
import { IException } from "../../models/osd";
import {
  IFilterForm,
  TTerminal,
} from "../../AppState/overagesFilterFormActions";

function Exceptions() {
  const dispatch = useDispatch();
  const location = useLocation();
  const tab = determineTab(location.pathname);
  const pageObject = useSelector(pageSelector);
  const sortModel = useSelector(sortSelector);
  const filterParams = useSelector(filterFromSelector);
  const { currentTerminalValue, currentTabPosition, terminals, tabPosition } =
    useSelector((state: AppState) => ({
      currentTerminalValue: state.activeTerminal,
      currentTabPosition: state.tabPosition,
      terminals: state.terminalMaster,
      tabPosition: state.tabPosition,
    }));

  const [rowCount, setRowCount] = useState(0);
  const [exceptions, setExceptions] = useState<IException[]>([]);
  const [loading, setLoading] = useState(false);
  const [to, setTo] = useState("");
  const [filterCriteria, setFilterCriteria] = useState("");
  const abortControllerRef = useRef<AbortController | null>(null);

  // Helper function to generate terminal list
  const getTerminalList = (
    currentTerminal: TTerminal,
    allTerminals: TTerminal[],
  ) => {
    // If currentTerminal?.region is undefined or null, return all terminals with the same region
    if (!currentTerminal?.region) {
      return allTerminals
        .filter((t) => t.region === currentTerminal.terminalId)
        .map((t) => t.terminalId);
    }

    // Otherwise, return an array containing only the current terminal's terminalId
    return [currentTerminal.terminalId];
  };

  // Helper function to get exception type
  const getExceptionType = (filterParams: IFilterForm, tab: ETab) => {
    // If filterParams.osdType is defined and non-empty, return it directly
    if (filterParams?.osdType?.length) {
      return filterParams.osdType;
    }

    // Otherwise, return the appropriate array based on the tab value
    const tabToOdsTypesMap = {
      [ETab.myAssignments]: ALL_OSD_TYPES,
      [ETab.dashboard]: ALL_OSD_TYPES,
      [ETab.overages]: OVERAGE_OSD_TYPES,
      [ETab.shortages]: SHORTAGE_OSD_TYPES,
      [ETab.damages]: DAMAGE_OSD_TYPE,
      [ETab.onHandDelivery]: ON_HAND_DELIVERY_OSD_TYPES,
      [ETab.misdelivery]: MISDELIVERY_OSD_TYPES,
    } as const;

    const osdTypes =
      tabToOdsTypesMap[tab as keyof typeof tabToOdsTypesMap] ?? [];
    return osdTypes;
  };

  // Memoize columnVisibilityModel, terminal list and exception type
  const terminalList = useMemo(
    () => getTerminalList(currentTerminalValue, terminals),
    [currentTerminalValue, terminals],
  );
  const exceptionType = useMemo(
    () => getExceptionType(filterParams, tab),
    [filterParams, tab],
  );

  const columnVisibilityModel = useMemo(() => {
    // Show Current Location only if at least one exception has osdType OV or NB on My Assignments tab
    const showExceptionLocation =
      currentTabPosition === ETab.myAssignments
        ? exceptions.some((ex) => ex.osdType === "OV" || ex.osdType === "NB")
        : true; // or set to your default for other tabs
    return {
      exceptionLocation: showExceptionLocation, // Add this line for the column
    };
  }, [currentTabPosition, exceptions]);

  // Fetching exception data
  const fetchExceptionList = () => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    setLoading(true);
    const params: any = getParams();
    let action = "list";
    let successHandler = handleSuccess;
    if (to.trim()) {
      action = "export";

      successHandler = handleExportSuccess;
      params.exportProperties = {
        to: to.trim().split(","),
      };
      params.limit = 5000; // Set a high limit for export
    } else {
      setExceptions([]);
    }
    setFilterCriteria(getFilterCriteria(params));

    getExceptionList(params, action, abortControllerRef.current.signal)
      .then(successHandler)
      .catch(handleError);
  };

  const getParams = () => {
    const { page = 0, pageSize = 0 } = pageObject ?? {};
    const sortField = sortModel?.field;
    const sortOrder = sortModel?.sort;

    const entryDateFrom = getEntryDateFrom();
    const entryDateTo = getEntryDateTo();
    const od400ProNumber = getNumericValue(filterParams?.od400ProNumber ?? "");
    const osdNumber = getNumericValue(filterParams?.osdNumber ?? "");
    const assignedBy = getAssignedBy();
    const assignedTo = getAssignedTo();
    const destination = getDestination();
    const reporting = getReporting();

    // If osdType does NOT include "OV" or "NB" and sortField is "exceptionLocation", override sortField and sortOrder
    let finalSortField = sortField;
    let finalSortOrder = sortOrder;
    if (
      sortField === "exceptionLocation" &&
      (!Array.isArray(exceptionType) ||
        !exceptionType.some((type) => type === "OV" || type === "NB"))
    ) {
      finalSortField = "entryDate";
      finalSortOrder = "desc";
    }

    return {
      page: page + 1,
      limit: pageSize,
      sortField: finalSortField,
      sortOrder: finalSortOrder,
      search: filterParams?.search,
      origin: filterParams.origin!,
      entryDateFrom,
      entryDateTo,
      commodityType: filterParams.commodityType!,
      description: filterParams?.description,
      shipperName: filterParams?.shipperName,
      consigneeName: filterParams?.consigneeName,
      driverName: filterParams?.driverName,
      dockWorkerName: filterParams?.dockWorkerName,
      proNumber: getNumericValue(filterParams?.proNumber ?? ""),
      entryBy: _.get(filterParams, "entryUser.as400Id", "").trim(),
      values: filterParams.values!,
      status: filterParams.status!,
      osdType: exceptionType,
      od400ProNumber,
      osdNumber,
      assignedBy,
      assignedTo,
      destination,
      reporting,
      selectFields: SEARCHABLE_FIELDS[tab],
      searchFields: SEARCHABLE_FIELDS[tab],
    };
  };

  const getEntryDateFrom = () =>
    filterParams?.date === "CUS"
      ? filterParams.fromDate
      : DateHelper.startDateSelector(filterParams?.date ?? "");

  const getEntryDateTo = () =>
    filterParams?.date === "CUS"
      ? filterParams.toDate
      : DateHelper.endDateSelector(filterParams?.date ?? "");

  const getNumericValue = (value?: string) => (value ? +value : null);

  const getValidValue = (...values: (string | undefined | null)[]) =>
    values?.find((val) => typeof val === "string" && val.trim()) ?? "";

  const getAssignedBy = () =>
    tab === ETab.myAssignments
      ? getValidValue(
          filterParams?.assignedBy?.as400Id,
          filterParams?.assignedBy?.nameFullLc,
        )?.trim()
      : undefined;

  const getAssignedTo = () =>
    tab === ETab.myAssignments
      ? username()
      : getValidValue(
          filterParams?.assignedTo?.as400Id,
          filterParams?.assignedTo?.nameFullLc,
        )?.trim();

  const getDestination = () => {
    if ([ETab.overages, ETab.myAssignments, ETab.dashboard].includes(tab)) {
      return filterParams.destination!;
    }
    if (currentTerminalValue?.terminalId === "All") {
      return undefined;
    }
    if (terminalList.length > 0) {
      return terminalList;
    }
    return [userTerminal()];
  };

  const getReporting = () => {
    if (
      [
        ETab.shortages,
        ETab.damages,
        ETab.onHandDelivery,
        ETab.myAssignments,
        ETab.dashboard,
        ETab.misdelivery,
      ].includes(tab)
    ) {
      return filterParams.reporting!;
    }
    return currentTerminalValue?.terminalId === "All"
      ? undefined
      : terminalList;
  };

  const getFilterCriteria = (params: any) =>
    filterCriteriaValue(
      { ...params, entryDateFrom: filterParams?.date },
      tab,
      !filterParams.osdType || filterParams?.osdType?.length < 1,
    );
  const handleSuccess = (response: any) => {
    setTo("");
    setExceptions(response?.data ?? []);
    setRowCount(response?.total ?? 0);
    setLoading(false);
  };

  const handleExportSuccess = (response: any) => {
    setTo("");
    setLoading(false);
    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.SUCCESS,
        "Export successful. An email with all exported records has been sent.",
        Date.now().toString(),
      ),
    );
  };

  const handleError = (error: any) => {
    // Ignore abort errors (user-initiated cancellation)
    if (error?.name === "AbortError") {
      return;
    }
    setTo("");
    setLoading(false);
    dispatch(
      PopoutNotification(NOTIFICATION_TYPE.ERROR, error, Date.now().toString()),
    );
  };

  useEffect(() => {
    if (tabPosition === tab) fetchExceptionList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTerminalValue, pageObject, sortModel, filterParams, tabPosition]);

  useEffect(() => {
    if (tabPosition === tab && to?.trim() !== "") fetchExceptionList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);

  useEffect(() => {
    dispatch(FooterTabPosition(tab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, dispatch]);

  // Cleanup: Cancel any pending requests on component unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="layout overage-layout">
      <div className="filter-criteria" data-testid="filter-criteria">
        Filter Applied: {filterCriteria || "None"}
      </div>
      <DynamicGrid
        id="osdNumber"
        checkboxSelection
        key={`${currentTerminalValue.terminalId}-${tab}`}
        rows={exceptions}
        tab={tab}
        setExportTo={setTo}
        loading={loading}
        rowCount={rowCount}
        onSuccessFullyAssigned={fetchExceptionList}
        columnVisibilityModel={columnVisibilityModel}
      />
    </div>
  );
}

export default Exceptions;
